import { useState, useCallback } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { FileDropzone } from '@/shared/components/FileDropzone'
import { LoadingSpinner } from '@/shared/components/ui/LoadingSpinner'
import { CustodyResultPanel } from './CustodyResultPanel'
import { apiClient } from '@/shared/api/client'
import { computeFileSHA256 } from '@/shared/lib/crypto'
import { API_ENDPOINTS, getCustodyUrl } from '@/shared/lib/constants'
import type { CustodyResponse, CustodyRequest } from '@/shared/types/api.types'

type CustodyState = 'idle' | 'hashing' | 'generating' | 'success' | 'error'

// Generate UUID v4 locally
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

interface DocumentCustodyProps {
  className?: string
}

const DOCUMENT_TYPES = [
  { value: 'carta_documento', label: 'Carta Documento' },
  { value: 'contrato', label: 'Contrato' },
  { value: 'poder', label: 'Poder' },
  { value: 'acta', label: 'Acta' },
  { value: 'resolucion', label: 'Resolución' },
  { value: 'otro', label: 'Otro' },
]

export function DocumentCustody({ className = '' }: DocumentCustodyProps) {
  const [state, setState] = useState<CustodyState>('idle')
  const [file, setFile] = useState<File | null>(null)
  const [fileHash, setFileHash] = useState<string | null>(null)
  const [result, setResult] = useState<CustodyResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Form fields
  const [documentTitle, setDocumentTitle] = useState('')
  const [documentType, setDocumentType] = useState('')
  const [documentDescription, setDocumentDescription] = useState('')
  const [legalContext, setLegalContext] = useState('')
  const [recipientName, setRecipientName] = useState('')
  const [recipientId, setRecipientId] = useState('')
  const [showOptionalFields, setShowOptionalFields] = useState(false)

  const handleFileSelect = useCallback(async (selectedFile: File) => {
    setFile(selectedFile)
    setError(null)
    setState('hashing')

    try {
      const hash = await computeFileSHA256(selectedFile)
      setFileHash(hash)
      setState('idle')
    } catch (err) {
      console.error('Error computing hash:', err)
      setError('Error al calcular el hash del archivo')
      setState('error')
    }
  }, [])

  const handleSubmit = async () => {
    if (!file || !fileHash) return

    setState('generating')
    setError(null)

    // Generate everything locally first
    const custodyId = generateUUID()
    const custodyTimestamp = new Date().toISOString()
    const verificationUrl = getCustodyUrl(custodyId)

    // Build local result (works offline)
    const localResult: CustodyResponse = {
      custody_id: custodyId,
      custody_timestamp: custodyTimestamp,
      file_hash: fileHash,
      hash_algorithm: 'SHA-256',
      original_filename: file.name,
      document_title: documentTitle || undefined,
      document_type: documentType || undefined,
      jurisdiction: 'AR',
      verification_url: verificationUrl,
      qr_code_base64: '', // Will use react-qr-code in result panel
      signwiz_version: '1.0.0',
    }

    // Try to save to server (optional, non-blocking)
    try {
      const request: CustodyRequest = {
        file_hash: fileHash,
        original_filename: file.name,
        file_size_bytes: file.size,
        document_title: documentTitle || undefined,
        document_type: documentType || undefined,
        document_description: documentDescription || undefined,
        legal_context: legalContext || undefined,
        recipient_name: recipientName || undefined,
        recipient_id: recipientId || undefined,
        jurisdiction: 'AR',
      }

      const response = await apiClient.post<CustodyResponse>(
        API_ENDPOINTS.REGISTER_CUSTODY,
        request
      )

      // Use server response if available (has PDF)
      setResult(response.data)
    } catch (err) {
      console.warn('Could not save to server, using local result:', err)
      // Use local result anyway
      setResult(localResult)
    }

    setState('success')
  }

  const handleReset = () => {
    setFile(null)
    setFileHash(null)
    setResult(null)
    setError(null)
    setDocumentTitle('')
    setDocumentType('')
    setDocumentDescription('')
    setLegalContext('')
    setRecipientName('')
    setRecipientId('')
    setState('idle')
  }

  // Show result panel if successful
  if (state === 'success' && result && file) {
    return <CustodyResultPanel result={result} originalFile={file} onReset={handleReset} />
  }

  return (
    <div className={`w-full max-w-3xl mx-auto space-y-6 ${className}`}>
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-legal-900 dark:text-white">
          Custodia Documental
        </h1>
        <p className="mt-2 text-legal-600 dark:text-legal-300">
          Registra la huella digital de tu documento para probar su existencia en una fecha determinada
        </p>
      </div>

      {/* File Dropzone */}
      <div className="card">
        <h2 className="text-lg font-semibold text-legal-900 dark:text-white mb-4">
          1. Selecciona el documento
        </h2>
        <FileDropzone
          onFileSelect={handleFileSelect}
          disabled={state === 'hashing' || state === 'generating'}
        />

        {/* File Info */}
        {file && (
          <div className="mt-4 p-4 bg-legal-50 dark:bg-legal-700 rounded-lg">
            <p className="text-sm text-legal-700 dark:text-legal-300">
              <span className="font-medium">Archivo:</span> {file.name}
            </p>
            <p className="text-sm text-legal-700 dark:text-legal-300">
              <span className="font-medium">Tamaño:</span> {(file.size / 1024).toFixed(1)} KB
            </p>
            {state === 'hashing' && (
              <div className="flex items-center gap-2 mt-2 text-blue-600 dark:text-blue-400">
                <LoadingSpinner size="sm" />
                <span className="text-sm">Calculando hash...</span>
              </div>
            )}
            {fileHash && (
              <p className="text-xs text-legal-500 dark:text-legal-400 mt-2 font-mono break-all">
                <span className="font-medium">SHA-256:</span> {fileHash}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Optional Form Fields (Collapsible) */}
      {fileHash && (
        <div className="card">
          <button
            type="button"
            onClick={() => setShowOptionalFields(!showOptionalFields)}
            className="w-full flex items-center justify-between text-left"
          >
            <h2 className="text-lg font-semibold text-legal-900 dark:text-white">
              2. Información del documento (opcional)
            </h2>
            {showOptionalFields ? (
              <ChevronUp className="w-5 h-5 text-legal-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-legal-500" />
            )}
          </button>

          {showOptionalFields && (
            <div className="space-y-4 mt-4 pt-4 border-t border-legal-200 dark:border-legal-600">
              <div>
                <label className="block text-sm font-medium text-legal-700 dark:text-legal-300 mb-1">
                  Título del documento
                </label>
                <input
                  type="text"
                  value={documentTitle}
                  onChange={(e) => setDocumentTitle(e.target.value)}
                  placeholder="ej: Intimación de pago - Contrato Alquiler"
                  className="w-full px-3 py-2 border border-legal-300 dark:border-legal-600 rounded-lg bg-white dark:bg-legal-700 text-legal-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-legal-700 dark:text-legal-300 mb-1">
                  Tipo de documento
                </label>
                <select
                  value={documentType}
                  onChange={(e) => setDocumentType(e.target.value)}
                  className="w-full px-3 py-2 border border-legal-300 dark:border-legal-600 rounded-lg bg-white dark:bg-legal-700 text-legal-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Seleccionar tipo...</option>
                  {DOCUMENT_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-legal-700 dark:text-legal-300 mb-1">
                    Nombre del destinatario
                  </label>
                  <input
                    type="text"
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                    placeholder="ej: Juan Perez"
                    className="w-full px-3 py-2 border border-legal-300 dark:border-legal-600 rounded-lg bg-white dark:bg-legal-700 text-legal-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-legal-700 dark:text-legal-300 mb-1">
                    DNI/CUIT del destinatario
                  </label>
                  <input
                    type="text"
                    value={recipientId}
                    onChange={(e) => setRecipientId(e.target.value)}
                    placeholder="ej: 12.345.678"
                    className="w-full px-3 py-2 border border-legal-300 dark:border-legal-600 rounded-lg bg-white dark:bg-legal-700 text-legal-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-legal-700 dark:text-legal-300 mb-1">
                  Contexto legal
                </label>
                <input
                  type="text"
                  value={legalContext}
                  onChange={(e) => setLegalContext(e.target.value)}
                  placeholder="ej: Intimación previa a demanda judicial"
                  className="w-full px-3 py-2 border border-legal-300 dark:border-legal-600 rounded-lg bg-white dark:bg-legal-700 text-legal-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-legal-700 dark:text-legal-300 mb-1">
                  Descripción adicional
                </label>
                <textarea
                  value={documentDescription}
                  onChange={(e) => setDocumentDescription(e.target.value)}
                  placeholder="Descripción detallada del documento..."
                  rows={3}
                  className="w-full px-3 py-2 border border-legal-300 dark:border-legal-600 rounded-lg bg-white dark:bg-legal-700 text-legal-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-600 rounded-lg">
          <p className="text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Submit Button */}
      {fileHash && (
        <div className="flex gap-4">
          <button
            onClick={handleSubmit}
            disabled={state === 'generating'}
            className="btn-primary flex-1 flex items-center justify-center gap-2"
          >
            {state === 'generating' ? (
              <>
                <LoadingSpinner size="sm" />
                Generando constancia...
              </>
            ) : (
              'Generar Constancia'
            )}
          </button>
          <button onClick={handleReset} className="btn-secondary">
            Cancelar
          </button>
        </div>
      )}

      {/* Info Box */}
      <div className="p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-600 rounded-lg">
        <h3 className="font-semibold text-blue-900 dark:text-blue-300 mb-2">
          Cómo funciona
        </h3>
        <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1 list-disc list-inside">
          <li>El documento NO se sube a nuestros servidores (privacidad)</li>
          <li>Solo se registra la huella digital (hash SHA-256)</li>
          <li>Recibirás un código QR y PDF de constancia</li>
          <li>Podés insertar el QR en tu documento PDF original</li>
          <li>Cualquiera puede verificar la existencia del documento escaneando el QR</li>
        </ul>
      </div>
    </div>
  )
}

export default DocumentCustody
